import { parseFiniteNumber } from '@/lib/number';
import type {
  CleanerAbsenceDeletePayload,
  CleanerAbsenceListFilters,
  CleanerAbsenceMutationResponse,
  CleanerAbsenceRecord,
  CleanerAbsenceUpsertPayload,
  OpsJobMutationResponse,
  OpsJobReassignPayload,
  OpsRuleUpsertPayload,
  OpsSiteUpsertPayload,
  OpsUserCreatePayload,
  OpsUserRecord,
  OpsUserUpdatePayload,
} from '../ops/api';

type ApiEnvelope<T> = T & {
  error?: string;
  message?: string;
};

export type OpsBundleUpsertInput = {
  site: OpsSiteUpsertPayload['site'];
  blocks: Array<OpsRuleUpsertPayload['block']>;
  replaceBlocks?: boolean;
};

export type OpsBundleMutationResult = {
  siteId: string;
  blockCount: number;
  hasFirstVisitOfMonthColumn: boolean;
  raw: unknown;
};

export type OpsSiteMutationResult = {
  siteId: string;
  raw: unknown;
};

export type OpsPreviewJob = {
  scheduledFor: string;
  siteId: string;
  siteName: string;
  blockId: string;
  blockSourceCode: string;
  label: string;
  area: string;
  jobKind: string;
  status: string;
  isoWeekday: number;
  isoWeek: number;
  totalPoints: number;
  raw: Record<string, unknown>;
};

export type OpsPreviewBlock = {
  sourceCode: string;
  area: string;
  label: string;
  jobKind: string;
  points: number;
  structuredTasks: Array<{ id: string; label: string; points: number }>;
  sortOrder: number;
};

export type OpsPreviewVisit = {
  visitKey: string;
  siteId: string;
  siteName: string;
  scheduledFor: string;
  isoWeekday: number;
  isoWeek: number;
  totalPoints: number;
  blocks: OpsPreviewBlock[];
};

export type OpsPreviewResult = {
  year: number;
  month: number;
  jobs: OpsPreviewJob[];
  visits: OpsPreviewVisit[];
  countsBySite: Array<{
    siteId: string;
    siteName: string;
    recurring: number;
    extra: number;
    total: number;
  }>;
  raw: unknown;
};

export type OpsGenerateResult = OpsPreviewResult & {
  generationRuns: Array<{
    id: string;
    targetYear: number;
    targetMonth: number;
    status: string;
  }>;
};

export type OpsRulesLoadResult = {
  raw: unknown;
};

export type PlanningUserRecord = OpsUserRecord;

type RuleListResponse = {
  sites: Array<Record<string, unknown>>;
  workBlocks: Array<Record<string, unknown>>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown, keys: string[]): string | null {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }
  return null;
}

function readNumber(value: unknown, keys: string[]): number | null {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    const parsed = parseFiniteNumber(value[key]);
    if (parsed != null) {
      return parsed;
    }
  }
  return null;
}

function readArray(value: unknown, keys: string[]): unknown[] | null {
  if (!isRecord(value)) return null;
  for (const key of keys) {
    const candidate = value[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }
  return null;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (isRecord(payload)) {
    const message = readString(payload, ['message', 'error', 'details']);
    if (message) return message;
  }

  return fallback;
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

export function normalizeTasksForSave(block: {
  structured_tasks: Array<{ id: string; label: string; points: number }>;
}): { structured_tasks: Array<{ id: string; label: string; points: number }>; points: number } {
  const structured_tasks = block.structured_tasks
    .map((task) => ({
      id: task.id,
      label: task.label.trim(),
      points: task.points,
    }))
    .filter((task) => task.label.length > 0);
  const points = Math.round(structured_tasks.reduce((sum, task) => sum + task.points, 0) * 100) / 100;

  return { structured_tasks, points };
}

function isUuidLike(value: unknown): value is string {
  return typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function sanitizeSiteForSave(site: OpsSiteUpsertPayload['site']): OpsSiteUpsertPayload['site'] {
  const next = { ...site };
  if (!isUuidLike(next.id)) {
    delete next.id;
  }
  return next;
}

function sanitizeBlockForSave(
  block: OpsRuleUpsertPayload['block'],
  siteId: string,
): OpsRuleUpsertPayload['block'] {
  const next = {
    ...block,
    site_id: siteId,
  };

  if (!isUuidLike(next.id)) {
    delete next.id;
  }

  return next;
}

function parseRuleListResponse(response: unknown): RuleListResponse {
  return {
    sites: (readArray(response, ['sites']) ?? []).filter((item): item is Record<string, unknown> => isRecord(item)),
    workBlocks: (
      readArray(response, ['workBlocks'])
      ?? readArray(response, ['work_blocks'])
      ?? []
    ).filter((item): item is Record<string, unknown> => isRecord(item)),
  };
}

function buildCountsFromJobs(jobs: OpsPreviewJob[]) {
  const countsBySite = new Map<string, { siteId: string; siteName: string; recurring: number; extra: number; total: number }>();

  for (const job of jobs) {
    const key = job.siteId || job.siteName || 'unknown';
    const current = countsBySite.get(key) ?? {
      siteId: job.siteId,
      siteName: job.siteName,
      recurring: 0,
      extra: 0,
      total: 0,
    };

    current.total += 1;
    if (job.jobKind === 'extra') {
      current.extra += 1;
    } else {
      current.recurring += 1;
    }

    countsBySite.set(key, current);
  }

  return Array.from(countsBySite.values()).sort((a, b) => a.siteName.localeCompare(b.siteName));
}

function parsePreviewResult(
  response: unknown,
  input: { siteId?: string; siteIds?: string[]; year: number; month: number },
): OpsPreviewResult {
  const fallbackSiteId = input.siteId ?? input.siteIds?.[0] ?? '';
  const jobsPayload =
    readArray(response, ['jobs']) ??
    readArray(isRecord(response) ? response.data : null, ['jobs']) ??
    [];

  const visitsPayload =
    readArray(response, ['visits']) ??
    readArray(isRecord(response) ? response.data : null, ['visits']) ??
    [];

  const countsPayload = readArray(response, ['counts_by_site']) ?? [];

  const jobs = jobsPayload
    .map((item) => {
      if (!isRecord(item)) return null;

      return {
        scheduledFor: readString(item, ['scheduled_for', 'scheduledFor']) ?? '—',
        siteId: readString(item, ['site_id', 'siteId']) ?? fallbackSiteId,
        siteName: readString(item, ['site_name', 'siteName']) ?? '',
        blockId: readString(item, ['block_id', 'blockId']) ?? '',
        blockSourceCode: readString(item, ['block_source_code', 'source_code', 'sourceCode']) ?? '—',
        label: readString(item, ['block_label', 'label']) ?? '—',
        area: readString(item, ['block_area', 'area']) ?? '—',
        jobKind: readString(item, ['job_kind', 'job_type', 'kind']) ?? '—',
        status: readString(item, ['status']) ?? 'assigned',
        isoWeekday: readNumber(item, ['iso_weekday']) ?? 0,
        isoWeek: readNumber(item, ['iso_week']) ?? 0,
        totalPoints: readNumber(item, ['total_points', 'totalPoints']) ?? 0,
        raw: item,
      };
    })
    .filter((item): item is OpsPreviewJob => item !== null);

  const visits = visitsPayload
    .map((item) => {
      if (!isRecord(item)) return null;

      const blocksPayload = readArray(item, ['blocks']) ?? [];
      const blocks = blocksPayload
        .map((b) => {
          if (!isRecord(b)) return null;
          const tasksPayload = readArray(b, ['structured_tasks', 'structuredTasks']) ?? [];
          const structuredTasks = tasksPayload
            .map((t) => {
              if (!isRecord(t)) return null;
              return {
                id: readString(t, ['id']) ?? '',
                label: readString(t, ['label']) ?? '',
                points: readNumber(t, ['points']) ?? 0,
              };
            })
            .filter((t): t is { id: string; label: string; points: number } => t !== null);

          return {
            sourceCode: readString(b, ['source_code', 'sourceCode']) ?? '—',
            area: readString(b, ['area']) ?? '—',
            label: readString(b, ['label']) ?? '—',
            jobKind: readString(b, ['job_kind', 'jobKind']) ?? 'recurring',
            points: readNumber(b, ['points']) ?? 0,
            structuredTasks,
            sortOrder: readNumber(b, ['sort_order', 'sortOrder']) ?? 0,
          };
        })
        .filter((b): b is OpsPreviewBlock => b !== null);

      return {
        visitKey: readString(item, ['visit_key', 'visitKey']) ?? '',
        siteId: readString(item, ['site_id', 'siteId']) ?? fallbackSiteId,
        siteName: readString(item, ['site_name', 'siteName']) ?? '',
        scheduledFor: readString(item, ['scheduled_for', 'scheduledFor']) ?? '—',
        isoWeekday: readNumber(item, ['iso_weekday']) ?? 0,
        isoWeek: readNumber(item, ['iso_week']) ?? 0,
        totalPoints: readNumber(item, ['total_points', 'totalPoints']) ?? 0,
        blocks,
      };
    })
    .filter((item): item is OpsPreviewVisit => item !== null);

  return {
    year: input.year,
    month: input.month,
    jobs,
    visits,
    countsBySite: countsPayload
      .map((item) => {
        if (!isRecord(item)) return null;
        return {
          siteId: readString(item, ['site_id', 'siteId']) ?? fallbackSiteId,
          siteName: readString(item, ['site_name', 'siteName']) ?? '',
          recurring: readNumber(item, ['recurring']) ?? 0,
          extra: readNumber(item, ['extra']) ?? 0,
          total: readNumber(item, ['total']) ?? 0,
        };
      })
      .filter(
        (item): item is {
          siteId: string;
          siteName: string;
          recurring: number;
          extra: number;
          total: number;
        } => item !== null,
      ),
    raw: response,
  };
}

async function requestJson<T>(
  path: string,
  sessionToken: string | null,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');

  if (sessionToken) {
    headers.set('Authorization', `Bearer ${sessionToken}`);
  }

  const response = await fetch(path, {
    ...init,
    headers,
  });

  const rawText = await response.text();
  const parsed = rawText ? safeParse(rawText) : null;

  if (!response.ok) {
    throw new Error(extractMessage(parsed, `${response.status} ${response.statusText}`));
  }

  return parsed as T;
}

export async function loadOpsRules(siteId: string, sessionToken: string | null): Promise<OpsRulesLoadResult> {
  const response = await requestJson<ApiEnvelope<unknown>>(
    `/api/ops/rules?siteId=${encodeURIComponent(siteId)}`,
    sessionToken,
    { method: 'GET' },
  );

  return { raw: response };
}

export async function saveOpsBundle(
  input: OpsBundleUpsertInput,
  sessionToken: string | null,
): Promise<OpsBundleMutationResult> {
  const siteResponse = await requestJson<ApiEnvelope<unknown>>(
    '/api/ops/sites/upsert',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify({ site: sanitizeSiteForSave(input.site) }),
    },
  );

  const initialList = parseRuleListResponse(siteResponse);
  const persistedSiteId =
    readString(initialList.sites[0], ['id'])
    ?? (isUuidLike(input.site.id) ? input.site.id : null);

  if (!persistedSiteId) {
    throw new Error('Could not resolve persisted site ID after saving the site.');
  }

  const desiredSourceCodes = new Set(
    input.blocks
      .map((block) => block.source_code.trim())
      .filter(Boolean),
  );
  const desiredIds = new Set(
    input.blocks
      .map((block) => block.id)
      .filter(isUuidLike),
  );

  if (input.replaceBlocks) {
    const staleBlocks = initialList.workBlocks.filter((block) => {
      const blockId = readString(block, ['id']);
      const sourceCode = readString(block, ['source_code', 'sourceCode']);
      if (blockId && desiredIds.has(blockId)) {
        return false;
      }
      return Boolean(sourceCode) && !desiredSourceCodes.has(sourceCode as string);
    });

    for (const block of staleBlocks) {
      const blockId = readString(block, ['id']);
      if (!blockId) continue;
      await requestJson<ApiEnvelope<unknown>>(
        '/api/ops/rules/delete',
        sessionToken,
        {
          method: 'POST',
          body: JSON.stringify({ id: blockId }),
        },
      );
    }
  }

  for (const block of input.blocks) {
    await requestJson<ApiEnvelope<unknown>>(
      '/api/ops/rules/upsert',
      sessionToken,
      {
        method: 'POST',
        body: JSON.stringify({
          block: sanitizeBlockForSave(block, persistedSiteId),
        }),
      },
    );
  }

  const finalResponse = await requestJson<ApiEnvelope<unknown>>(
    `/api/ops/rules?siteId=${encodeURIComponent(persistedSiteId)}`,
    sessionToken,
    { method: 'GET' },
  );
  const finalList = parseRuleListResponse(finalResponse);

  return {
    siteId: persistedSiteId,
    blockCount: finalList.workBlocks.length,
    hasFirstVisitOfMonthColumn: finalList.workBlocks.some((block) => 'first_visit_of_month' in block),
    raw: finalResponse,
  };
}

export async function saveOpsSite(
  site: OpsSiteUpsertPayload['site'],
  sessionToken: string | null,
): Promise<OpsSiteMutationResult> {
  const response = await requestJson<ApiEnvelope<unknown>>(
    '/api/ops/sites/upsert',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify({ site: sanitizeSiteForSave(site) }),
    },
  );

  const parsed = parseRuleListResponse(response);
  const persistedSiteId =
    readString(parsed.sites[0], ['id'])
    ?? (isUuidLike(site.id) ? site.id : null);

  if (!persistedSiteId) {
    throw new Error('Could not resolve persisted site ID after saving the site.');
  }

  return {
    siteId: persistedSiteId,
    raw: response,
  };
}

export async function deleteOpsBundle(
  input: { siteId: string; blockIds?: string[]; deleteSite?: boolean },
  sessionToken: string | null,
): Promise<OpsBundleMutationResult> {
  const blockIds = toStringArray(input.blockIds);
  let raw: unknown = null;

  for (const blockId of blockIds) {
    await requestJson<ApiEnvelope<unknown>>(
      '/api/ops/rules/delete',
      sessionToken,
      {
        method: 'POST',
        body: JSON.stringify({ id: blockId }),
      },
    );
  }

  if (input.deleteSite) {
    raw = await requestJson<ApiEnvelope<unknown>>(
      '/api/ops/sites/delete',
      sessionToken,
      {
        method: 'POST',
        body: JSON.stringify({ id: input.siteId }),
      },
    );
  } else if (blockIds.length > 0) {
    raw = await requestJson<ApiEnvelope<unknown>>(
      `/api/ops/rules?siteId=${encodeURIComponent(input.siteId)}`,
      sessionToken,
      { method: 'GET' },
    );
  }

  return {
    siteId: input.siteId,
    blockCount: blockIds.length,
    hasFirstVisitOfMonthColumn: false,
    raw,
  };
}

export async function previewOpsJobs(
  input: { siteId: string; year: number; month: number },
  sessionToken: string | null,
): Promise<OpsPreviewResult> {
  const response = await requestJson<ApiEnvelope<unknown>>(
    '/api/ops/preview',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );

  const parsed = parsePreviewResult(response, input);

  return {
    ...parsed,
    countsBySite: parsed.countsBySite.length > 0 ? parsed.countsBySite : buildCountsFromJobs(parsed.jobs),
  };
}

export async function regenerateOpsJobs(
  input: { year: number; month: number; siteId: string },
  sessionToken: string | null,
): Promise<void> {
  await requestJson<ApiEnvelope<unknown>>(
    '/api/ops/generate',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
}

export async function listOpsUsers(sessionToken: string | null): Promise<PlanningUserRecord[]> {
  const response = await requestJson<ApiEnvelope<unknown>>(
    '/api/ops/users',
    sessionToken,
    { method: 'GET' },
  );

  const usersPayload = readArray(response, ['users']) ?? [];
  return usersPayload
    .map((item) => {
      if (!isRecord(item)) return null;
      return {
        id: readString(item, ['id']) ?? '',
        email: readString(item, ['email']) ?? '',
        full_name: readString(item, ['full_name', 'fullName']),
        role: (readString(item, ['role']) as PlanningUserRecord['role'] | null) ?? 'cleaner',
        active: Boolean(item.active ?? true),
        created_at: readString(item, ['created_at', 'createdAt']) ?? '',
        updated_at: readString(item, ['updated_at', 'updatedAt']) ?? '',
      };
    })
    .filter((item): item is PlanningUserRecord => item !== null);
}

export async function createOpsUser(
  input: OpsUserCreatePayload,
  sessionToken: string | null,
): Promise<PlanningUserRecord> {
  const response = await requestJson<ApiEnvelope<unknown>>(
    '/api/ops/users/create',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );

  const responseRecord = isRecord(response) ? response : null;
  const userPayload = responseRecord && isRecord(responseRecord['user']) ? responseRecord['user'] : null;
  const user = readArray({ users: userPayload ? [userPayload] : [] }, ['users'])
    .map((item) => {
      if (!isRecord(item)) return null;
      return {
        id: readString(item, ['id']) ?? '',
        email: readString(item, ['email']) ?? '',
        full_name: readString(item, ['full_name', 'fullName']),
        role: (readString(item, ['role']) as PlanningUserRecord['role'] | null) ?? 'cleaner',
        active: Boolean(item.active ?? true),
        created_at: readString(item, ['created_at', 'createdAt']) ?? '',
        updated_at: readString(item, ['updated_at', 'updatedAt']) ?? '',
      };
    })
    .find((item): item is PlanningUserRecord => item !== null);

  if (!user) {
    throw new Error('Could not parse created user.');
  }

  return user;
}

export async function updateOpsUser(
  input: OpsUserUpdatePayload,
  sessionToken: string | null,
): Promise<PlanningUserRecord> {
  const response = await requestJson<ApiEnvelope<unknown>>(
    '/api/ops/users/update',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );

  const responseRecord = isRecord(response) ? response : null;
  const userPayload = responseRecord && isRecord(responseRecord['user']) ? responseRecord['user'] : null;
  const user = readArray({ users: userPayload ? [userPayload] : [] }, ['users'])
    .map((item) => {
      if (!isRecord(item)) return null;
      return {
        id: readString(item, ['id']) ?? '',
        email: readString(item, ['email']) ?? '',
        full_name: readString(item, ['full_name', 'fullName']),
        role: (readString(item, ['role']) as PlanningUserRecord['role'] | null) ?? 'cleaner',
        active: Boolean(item.active ?? true),
        created_at: readString(item, ['created_at', 'createdAt']) ?? '',
        updated_at: readString(item, ['updated_at', 'updatedAt']) ?? '',
      };
    })
    .find((item): item is PlanningUserRecord => item !== null);

  if (!user) {
    throw new Error('Could not parse updated user.');
  }

  return user;
}

export async function approveOpsJob(
  input: { jobId: string },
  sessionToken: string | null,
): Promise<OpsJobMutationResponse> {
  return requestJson<OpsJobMutationResponse>(
    '/api/ops/jobs/approve',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
}

export async function reassignOpsJob(
  input: OpsJobReassignPayload,
  sessionToken: string | null,
): Promise<OpsJobMutationResponse> {
  return requestJson<OpsJobMutationResponse>(
    '/api/ops/jobs/reassign',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
}

export async function listCleanerAbsences(
  filters: CleanerAbsenceListFilters,
  sessionToken: string | null,
): Promise<CleanerAbsenceRecord[]> {
  const qs = new URLSearchParams();
  if (filters.userId) qs.set('userId', filters.userId);
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  const path = qs.toString() ? `/api/ops/absences?${qs}` : '/api/ops/absences';
  const response = await requestJson<{ absences: CleanerAbsenceRecord[] }>(path, sessionToken, { method: 'GET' });
  return response.absences;
}

export async function upsertCleanerAbsence(
  payload: CleanerAbsenceUpsertPayload,
  sessionToken: string | null,
): Promise<CleanerAbsenceMutationResponse> {
  return requestJson<CleanerAbsenceMutationResponse>(
    '/api/ops/absences/upsert',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteCleanerAbsence(
  payload: CleanerAbsenceDeletePayload,
  sessionToken: string | null,
): Promise<{ affectedJobIds: string[] }> {
  return requestJson<{ affectedJobIds: string[] }>(
    '/api/ops/absences/delete',
    sessionToken,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

// -------------------- Pricing --------------------

export interface SitePricingPeriod {
  id: string;
  site_id: string;
  effective_from: string;
  effective_to: string | null;
  month_mask: number[];
  price_ex_moms_sek: number;
  moms_rate: number;
  price_inc_moms_sek: number | null;
  fortnox_article_number: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface SitePricingUpsertPayload {
  pricing: {
    id?: string | null;
    siteId: string;
    effectiveFrom: string;
    effectiveTo?: string | null;
    monthMask?: number[];
    priceExMomsSek: number;
    momsRate?: number;
    fortnoxArticleNumber?: string | null;
    note?: string | null;
  };
}

export async function listSitePricing(
  siteId: string,
  sessionToken: string | null,
): Promise<SitePricingPeriod[]> {
  const res = await requestJson<{ periods: SitePricingPeriod[] }>(
    `/api/ops/sites/${encodeURIComponent(siteId)}/pricing`,
    sessionToken,
    { method: 'GET' },
  );
  return res.periods ?? [];
}

export async function upsertSitePricing(
  payload: SitePricingUpsertPayload,
  sessionToken: string | null,
): Promise<SitePricingPeriod> {
  const res = await requestJson<{ period: SitePricingPeriod }>(
    '/api/ops/sites/pricing/upsert',
    sessionToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return res.period;
}

export async function deleteSitePricing(
  id: string,
  sessionToken: string | null,
): Promise<void> {
  await requestJson<{ deleted: boolean }>(
    '/api/ops/sites/pricing/delete',
    sessionToken,
    { method: 'POST', body: JSON.stringify({ id }) },
  );
}

// -------------------- Extras month view --------------------

export interface ExtrasMonthFilters {
  month: string;
  cleanerId?: string | null;
  siteId?: string | null;
  status?: string | null;
  cadenceType?: 'ONE_OFF' | 'EXACT_MONTHS' | null;
}

export interface ExtrasRow {
  job_id: string;
  job_block_id: string;
  work_block_id: string;
  scheduled_for: string;
  source_code: string;
  label: string;
  area: string;
  cadence: string;
  cleaner: { id: string; name: string } | null;
  status: string;
  block_status: string;
  points: number;
  price_ex_moms_sek: number | null;
  price_inc_moms_sek: number | null;
  moms_rate: number;
  required_photos: number;
  photos_count: number;
  structured_tasks: unknown;
  approved_at: string | null;
  approved_by: string | null;
}

export interface ExtrasSiteBucket {
  site_id: string;
  site_name: string;
  extras: ExtrasRow[];
  totals: { count: number; points: number; price_ex_moms: number };
}

export interface ExtrasMonthResponse {
  month: string;
  filters: ExtrasMonthFilters;
  sites: ExtrasSiteBucket[];
  grandTotals: {
    count: number;
    points: number;
    price_ex_moms: number;
    price_inc_moms: number;
    byStatus: Record<string, number>;
  };
}

export async function listExtrasMonth(
  filters: ExtrasMonthFilters,
  sessionToken: string | null,
): Promise<ExtrasMonthResponse> {
  const qs = new URLSearchParams({ month: filters.month });
  if (filters.cleanerId) qs.set('cleanerId', filters.cleanerId);
  if (filters.siteId) qs.set('siteId', filters.siteId);
  if (filters.status) qs.set('status', filters.status);
  if (filters.cadenceType) qs.set('cadenceType', filters.cadenceType);
  return requestJson<ExtrasMonthResponse>(
    `/api/ops/extras?${qs.toString()}`,
    sessionToken,
    { method: 'GET' },
  );
}

// -------------------- Ledger --------------------

export interface LedgerEntry {
  id: string;
  user_id: string;
  job_id: string | null;
  job_block_id: string | null;
  site_id: string | null;
  site_name: string | null;
  cleaner_name: string | null;
  kind: 'recurring' | 'extra' | 'adjustment';
  points: number;
  approved_at: string;
  approved_by: string | null;
  paid_out_at: string | null;
  paid_out_by: string | null;
  paid_out_batch_id: string | null;
  note: string | null;
}

export interface LedgerResponse {
  entries: LedgerEntry[];
  totals: {
    points: number;
    byKind: Record<string, number>;
    bySite: Record<string, number>;
    pending: number;
    paid: number;
  };
}

export async function listCleanerPoints(
  userId: string,
  filters: { from?: string; to?: string; siteId?: string; kind?: string; paid?: string },
  sessionToken: string | null,
): Promise<LedgerResponse> {
  const qs = new URLSearchParams();
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.siteId) qs.set('siteId', filters.siteId);
  if (filters.kind) qs.set('kind', filters.kind);
  if (filters.paid) qs.set('paid', filters.paid);
  return requestJson<LedgerResponse>(
    `/api/ops/cleaners/${encodeURIComponent(userId)}/points?${qs.toString()}`,
    sessionToken,
    { method: 'GET' },
  );
}

export async function markCleanerPointsPaid(
  payload: { ledgerIds: string[]; batchId?: string | null; paidOutAt?: string | null },
  sessionToken: string | null,
): Promise<{ updated: number; skipped: number }> {
  return requestJson<{ updated: number; skipped: number }>(
    '/api/ops/cleaners/points/mark-paid',
    sessionToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function adjustCleanerPoints(
  payload: { userId: string; points: number; note: string; siteId?: string | null; jobId?: string | null },
  sessionToken: string | null,
): Promise<{ entry: LedgerEntry }> {
  return requestJson<{ entry: LedgerEntry }>(
    '/api/ops/cleaners/points/adjust',
    sessionToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

// -------------------- Invoicing --------------------

export interface InvoiceLine {
  id: string;
  draft_id: string;
  line_type: 'recurring' | 'extra' | 'manual';
  description: string;
  quantity: number;
  price_ex_moms: number;
  moms_rate: number;
  fortnox_article_number: string | null;
  source_pricing_period_id: string | null;
  source_work_block_id: string | null;
  source_job_block_id: string | null;
}

export interface InvoiceDraft {
  id: string | null;
  site_id: string;
  site_name: string;
  fortnox_customer_number: string | null;
  period_year: number;
  period_month: number;
  status: 'pending' | 'exported' | 'skipped';
  base_amount_ex_moms: number;
  extras_amount_ex_moms: number;
  total_ex_moms: number | null;
  moms_rate: number;
  total_inc_moms: number | null;
  admin_note: string | null;
  exported_at: string | null;
  exported_by: string | null;
  fortnox_invoice_number: string | null;
  created_at: string | null;
  updated_at: string | null;
  pending_extras_count: number;
  pending_extras_amount_ex_moms: number;
  lines: InvoiceLine[];
}

export async function listInvoiceDrafts(
  year: number,
  month: number,
  sessionToken: string | null,
): Promise<InvoiceDraft[]> {
  const res = await requestJson<{ drafts: InvoiceDraft[] }>(
    `/api/ops/invoicing/drafts?year=${year}&month=${month}`,
    sessionToken,
    { method: 'GET' },
  );
  return res.drafts ?? [];
}

export async function markInvoiceDraftExported(
  payload: { siteId: string; year: number; month: number; fortnoxInvoiceNumber?: string | null; note?: string | null },
  sessionToken: string | null,
): Promise<{ draft: InvoiceDraft }> {
  return requestJson<{ draft: InvoiceDraft }>(
    '/api/ops/invoicing/sheets/mark-sent',
    sessionToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function skipInvoiceDraft(
  payload: { siteId: string; year: number; month: number; note: string },
  sessionToken: string | null,
): Promise<{ draft: InvoiceDraft }> {
  return requestJson<{ draft: InvoiceDraft }>(
    '/api/ops/invoicing/sheets/waive',
    sessionToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function updateInvoiceDraft(
  payload: { siteId: string; year: number; month: number; adminNote?: string | null },
  sessionToken: string | null,
): Promise<{ draft: InvoiceDraft }> {
  return requestJson<{ draft: InvoiceDraft }>(
    '/api/ops/invoicing/sheets/update',
    sessionToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export function getInvoiceDraftCsvUrl(
  siteId: string,
  year: number,
  month: number,
): string {
  return `/api/ops/invoicing/sheets/${encodeURIComponent(siteId)}/${year}/${month}/export.csv`;
}

// -------------------- Notifications --------------------

export interface NotificationRecord {
  id: string;
  kind: string;
  severity: 'info' | 'warning' | 'error';
  title: string;
  body: string | null;
  payload: Record<string, unknown>;
  scope_role: 'admin' | 'supervisor' | 'cleaner' | null;
  scope_user_id: string | null;
  site_id: string | null;
  dedup_key: string;
  seen_at: string | null;
  dismissed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationsResponse {
  notifications: NotificationRecord[];
  counts: { unseen: number; warning: number; error: number };
}

export async function listNotifications(
  sessionToken: string | null,
  includeDismissed = false,
): Promise<NotificationsResponse> {
  const qs = includeDismissed ? '?includeDismissed=true' : '';
  return requestJson<NotificationsResponse>(
    `/api/ops/notifications${qs}`,
    sessionToken,
    { method: 'GET' },
  );
}

export async function markNotificationSeen(id: string, sessionToken: string | null): Promise<void> {
  await requestJson<{ ok: true }>(
    `/api/ops/notifications/${encodeURIComponent(id)}/seen`,
    sessionToken,
    { method: 'POST' },
  );
}

export async function dismissNotification(id: string, sessionToken: string | null): Promise<void> {
  await requestJson<{ ok: true }>(
    `/api/ops/notifications/${encodeURIComponent(id)}/dismiss`,
    sessionToken,
    { method: 'POST' },
  );
}

export async function markAllNotificationsSeen(sessionToken: string | null): Promise<{ updated: number }> {
  return requestJson<{ updated: number }>(
    '/api/ops/notifications/read-all',
    sessionToken,
    { method: 'POST' },
  );
}

export async function refreshInvoicingWarnings(
  payload: { year?: number; month?: number },
  sessionToken: string | null,
): Promise<{ emitted: number; resolved: number }> {
  const qs = new URLSearchParams();
  if (payload.year) qs.set('year', String(payload.year));
  if (payload.month) qs.set('month', String(payload.month));
  return requestJson<{ emitted: number; resolved: number }>(
    `/api/ops/notifications/refresh-invoicing${qs.toString() ? `?${qs}` : ''}`,
    sessionToken,
    { method: 'POST' },
  );
}
